/**
 * Client-side "needs review" logic for the report table. Surfaces two things a
 * reviewer (Sales Ops) cares about before a report goes to Finance:
 *   - backfilled cells (filled from Coaching.com / the project-number lookup) → verify
 *   - required cells still blank → complete (in Salesforce)
 * Mirrors the .xlsx highlight intent so the on-screen review matches the file.
 */

import type { ReportRow } from '../types';

// Backend `enriched_fields` keys (FinanceReportRow) → table column ids (ReportRow).
const ENRICHED_KEY_TO_COL: Record<string, keyof ReportRow> = {
  accountName: 'account',
  engagementTemplate: 'template',
  status: 'status',
  netSuiteProjectId: 'projectId',
  coachee: 'coachee',
  coacheeLastName: 'coacheeLast',
  internalIdProject: 'internalId',
  affiliatePaidHourlyRate: 'rate',
  netSuiteOpportunityNumber: 'oppNum',
  sowEngagementStartDate: 'sowStart',
  sowEngagementEndDate: 'sowEnd',
  totalHoursBudget: 'hBudget',
  clientHourlyFee: 'clientFee',
  totalClientFeeBudget: 'feeBudget',
};

// Red "missing" stays aligned with the .xlsx: workbook.ts only pinks Vendor Name
// and Internal ID as NetSuite-load blockers, so those are the only red cells here.
const BLOCKING_COLS: (keyof ReportRow)[] = ['vendorName', 'internalId'];

// Softer SF gaps Sales Ops completes (not load-blockers) → amber, not red.
// `status` is excluded: mapDbReportRow coerces it to 'Active', so it's never blank.
const COMPLETE_COLS: (keyof ReportRow)[] = ['coachee', 'account', 'projectId'];

const ALL_REQUIRED = [...BLOCKING_COLS, ...COMPLETE_COLS];

const FIELD_LABELS: Partial<Record<keyof ReportRow, string>> = {
  vendorName: 'Vendor name',
  internalId: 'Internal ID',
  coachee: 'Coachee',
  account: 'Account',
  projectId: 'Project #',
};

export type CellFlag = 'enriched' | 'missing' | 'verify';

function isBlank(v: unknown): boolean {
  return v == null || v === '';
}

export function enrichedColSet(row: ReportRow): Set<keyof ReportRow> {
  const out = new Set<keyof ReportRow>();
  for (const k of row.enrichedFields ?? []) {
    const col = ENRICHED_KEY_TO_COL[k];
    if (col) out.add(col);
  }
  return out;
}

/**
 * Per-cell flag. Precedence: missing > verify > enriched. 'missing' (red) is
 * reserved for the workbook's load-blockers (Vendor Name / Internal ID) so the
 * table matches the .xlsx; softer SF gaps + checks are amber ('verify').
 */
export function cellFlag(
  col: keyof ReportRow,
  row: ReportRow,
  enriched: Set<keyof ReportRow>,
): CellFlag | null {
  if (BLOCKING_COLS.includes(col) && isBlank(row[col])) return 'missing';
  if (COMPLETE_COLS.includes(col) && isBlank(row[col])) return 'verify';
  if (col === 'coacheeLast' && row.coacheeLastDerived && !isBlank(row.coacheeLast)) return 'verify';
  if (col === 'hours' && (row.hours ?? 0) > 10) return 'verify';
  if (enriched.has(col)) return 'enriched';
  return null;
}

/** Human labels for the required fields still blank on a row (for the drawer). */
export function missingFields(row: ReportRow): string[] {
  return ALL_REQUIRED.filter((c) => isBlank(row[c])).map((c) => FIELD_LABELS[c] ?? String(c));
}

export function rowNeedsReview(row: ReportRow): boolean {
  if ((row.enrichedFields ?? []).length > 0) return true;
  if (row.coacheeLastDerived && !isBlank(row.coacheeLast)) return true;
  if ((row.hours ?? 0) > 10) return true;
  return ALL_REQUIRED.some((c) => isBlank(row[c]));
}
